/**
 * Translation system for frontend text
 * Supports multiple languages based on child's language preference
 */

const translations = {
  en: {
    // Chat component
    welcome: "Welcome to RuleeAI",
    navChat: "Chat",
    navQuiz: "Quiz",
    navConversations: "Conversations",
    navSubjects: "School Classes",
    navParentDashboard: "Parent Dashboard",
    menu: "Menu",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    logout: "Logout",
    newChat: "New Chat",
    myConversations: "My Conversations",
    convoFilterAll: "All",
    convoFilterGeneral: "General Chat",
    convoFilterSubjectLearning: "Subject Learning",
    convoFilterSectionLearning: "Section Learning",
    convoFilterQuiz: "Quizzes",
    convoFilterTodaySection: "Today's Lessons",
    loadingConversations: "Loading conversations...",
    noConversationsYet: "No conversations yet. Start a new chat to begin!",
    startNewChat: "Start New Chat",
    untitledConversation: "Untitled Conversation",
    messageSingular: "message",
    messagePlural: "messages",
    selectConversationHint: "Select a conversation from the Conversations tab or start a new chat!",
    schoolClasses: "Subjects",
    subjectsDescription: "Choose a subject to start learning",
    loadingClasses: "Loading classes...",
    noClassesAssigned: "No classes assigned yet.",
    askParentAssignClasses: "Ask your parent to assign classes to you.",
    addTodaysSection: "Add Lesson",
    learnSection: "Learn Lesson",
    selectSection: "Select a Lesson to Learn",
    learnThis: "Learn This",
    back: "Back",
    generateQuiz: "Generate Quiz",
    send: "Send",
    newConversation: "New Conversation",
    typeMessage: "Type your message...",
    loading: "Loading...",
    errorSendingMessage: "Error sending message. Please try again.",
    pleaseFillFields: "Please fill in both topic and section fields.",
    you: "You",
    assistant: "Assistant",
    assistantTyping: "Assistant is typing...",
    closeConversation: "Close conversation",
    closeModal: "Close modal",
    closeAlert: "Close alert",
    closeReviewModal: "Close review modal",
    copyCode: "Copy code",
    switchToLightMode: "Switch to light mode",
    switchToDarkMode: "Switch to dark mode",
    loginToRulee: "Login to RuleeAI",
    username: "Username",
    password: "Password",
    loggingIn: "Logging in...",
    login: "Login",
    backToHome: "Back to home",
    noticeTitle: "Notice",
    okLabel: "OK",
    
    // Subject modal
    selectSubject: "Select a subject",
    topic: "Topic",
    section: "Section",
    enterTopic: "Enter the topic you learned",
    enterSection: "Enter the section you covered",
    submit: "Submit",
    cancel: "Cancel",
    addTodaysSectionDesc: "Add what you learned in class today",
    learnSectionDesc: "Create a lesson path and study step by step",
    generateQuizDesc: "Practice with a fun quiz game based on your progress",
    addTodaysSectionMessage: "I want to add a lesson for",
    helpUnderstandMessage: "Help me understand and remember what we learned today.",
    learnAboutMessage: "I want to learn about",
    createLearningPathMessage: "Create a learning path for me and help me understand the topics step by step.",
    noTopicsForQuiz: "No topics found for this subject. Please add topics by using 'Add Lesson' first.",
    quizSourceLabel: "Quiz source",
    quizSourceAuto: "Auto (best available)",
    quizSourceWeakTopics: "Weak topics only",
    quizSourceKnowledgeTopics: "Knowledge topics",
    quizSourceTodaySections: "Today's lessons",
    quizQuestionCountLabel: "Number of questions",
    quizGeneratingTitle: "Generating your new quiz...",
    quizGeneratingDescription: "Please wait while we prepare questions for you.",
    quizSectionsLabel: "Select sections for quiz",
    selectQuizSectionsValidation: "Please select at least one section.",
    noSectionsFound: "No lessons yet for this subject. Add a lesson first, then generate or learn from it.",
    quizNoActive: "No active quiz yet. Generate one from School Classes.",
    quizPageTitle: "Quiz",
    quizStartNewTitle: "Start New Quiz",
    quizStartNewAction: "Start New Quiz",
    quizShowSetup: "Show quiz setup",
    quizHideSetup: "Hide quiz setup",
    quizShowHistory: "Show previous quizzes",
    quizHideHistory: "Hide previous quizzes",
    quizShowActive: "Show active quizzes",
    quizHideActive: "Hide active quizzes",
    quizHistoryTitle: "Completed Quizzes",
    quizHistoryEmpty: "No completed quizzes yet.",
    quizActiveTitle: "Active Quizzes",
    quizActiveEmpty: "No active quizzes yet.",
    quizOpenActive: "Open Quiz",
    quizOpeningActive: "Opening quiz...",
    quizLoadingReview: "Loading quiz review...",
    quizReviewNext: "Next",
    quizReviewPrevious: "Previous",
    quizSubjectLabel: "Subject",
    backToSubjects: "Back to subjects",
    quizQuestions: "questions",
    quizResultTitle: "Quiz Result",
    quizScoreLabel: "score",
    quizTopicsToPractice: "Topics to practice",
    quizStatusCorrect: "Correct",
    quizStatusIncorrect: "Incorrect",
    quizYourAnswerLabel: "Your answer",
    quizNoAnswerGiven: "No answer given",
    quizCorrectAnswerLabel: "Correct answer",
    quizCorrectAnswerMissing: "Answer not provided",
    quizTextAnswerPlaceholder: "Type your answer...",
    submitQuiz: "Submit Quiz",
    cancelQuiz: "Cancel Quiz",
    quizCompleteAllQuestions: "Please answer all questions before submitting.",

    // Landing page
    landing: {
      hero: {
        eyebrow: "Built for families, not surveillance",
        headline: "AI your child can explore - with you still in the loop.",
        subheadline: "Rulee is a privacy-first AI companion for kids. Parents get helpful context about curiosity and wellbeing without reading private chats.",
        primaryCta: "Start with your family",
        secondaryCta: "See how it works",
        earlyAccess: "Join the first 100 families.",
        visualCaption: "A calmer way to stay connected at home",
        insightLabel: "Rulee Insight",
        insightText: "Today might be a good moment for a short walk together."
      },
      badges: {
        privacyFirst: "Privacy First",
        noAds: "No Ads",
        noDataSelling: "No Data Selling"
      },
      problem: {
        kicker: "The parent challenge",
        headline: "Kids are already using AI. Parents are the last to know.",
        point1: "Kids ask AI questions they never ask parents.",
        point2: "AI answers everything, but parents lose context.",
        point3: "Monitoring apps destroy trust.",
        transition: "Rulee fixes this without spying."
      },
      how: {
        kicker: "How it works",
        headline: "How Rulee works",
        step1Label: "Step 1",
        step1Title: "Kids explore safely",
        step1Body: "Children can ask AI questions and learn with age-appropriate boundaries.",
        step2Label: "Step 2",
        step2Title: "Parents get gentle insights",
        step2Body: "Instead of chat logs, parents see interests, patterns, and emotional signals.",
        step3Label: "Step 3",
        step3Title: "Rulee suggests connection",
        step3Body: "Parents receive simple suggestions to reconnect at everyday moments."
      },
      kidsDemo: {
        kicker: "Kids experience",
        headline: "Everything children can do in Rulee",
        subheadline: "An interactive preview of the child interface with real learning actions and quiz flow.",
        featureTitle: "Core features",
        flowTitle: "Learning flow",
        demoTitle: "Interactive demo",
        featureChatTitle: "Chat",
        featureChatBody: "Children can ask questions naturally and continue conversations safely.",
        featureConversationsTitle: "Conversations",
        featureConversationsBody: "They can revisit previous chats and continue where they left off.",
        featureSubjectsTitle: "School Subjects",
        featureSubjectsBody: "Children pick subjects, add a lesson, and start guided learning.",
        featureQuizTitle: "Quiz",
        featureQuizBody: "They generate quizzes from weak topics, knowledge topics, or today's lessons.",
        featureAddSectionTitle: "Add Lesson",
        featureAddSectionBody: "Capture what was learned in class and make it available for practice.",
        featureLearnSectionTitle: "Learn Lesson",
        featureLearnSectionBody: "Choose a section and get step-by-step explanations.",
        featureQuizHistoryTitle: "Quiz History & Review",
        featureQuizHistoryBody: "Review scores, correct answers, and topics to practice.",
        flow1Title: "Pick a subject",
        flow1Body: "Start from School Subjects and choose the class you want to practice.",
        flow2Title: "Add or learn a section",
        flow2Body: "Add a lesson or open a lesson-learning path before quiz generation.",
        flow3Title: "Generate a quiz",
        flow3Body: "Choose quiz source and question count, then generate an age-appropriate quiz.",
        flow4Title: "Submit and review",
        flow4Body: "Submit answers, see score, and review weak topics from quiz history.",
        demoTabSubjects: "School Subjects",
        demoTabConversations: "Conversations",
        demoTabQuiz: "Quiz",
        demoSubjectsTitle: "School Subjects panel",
        demoConversationsTitle: "Conversations panel",
        demoQuizTitle: "Quiz panel",
        demoGenerateQuiz: "Generate Quiz",
        demoAddSection: "Add Lesson",
        demoLearnSection: "Learn Lesson",
        demoGenerating: "Generating quiz...",
        demoQuizReady: "Quiz is ready",
        demoSourceLabel: "Source",
        demoQuestionsLabel: "Questions",
        demoSubjectLabel: "Subject",
        demoSourceAuto: "Auto (best available)",
        demoSourceWeak: "Weak topics",
        demoConversationsHint: "Recent conversations with type badges and date.",
        demoSubjectsHint: "Each subject supports Add Lesson, Learn Lesson, and Generate Quiz.",
        demoQuizHint: "Submit answers and review topics to practice."
      },
      difference: {
        kicker: "What makes Rulee different",
        headline: "Rulee is not a monitoring app.",
        monitoringCol: "Monitoring Apps",
        ruleeCol: "Rulee",
        row1Left: "Read messages",
        row1Right: "No message logs",
        row2Left: "Track everything",
        row2Right: "Only insights",
        row3Left: "Break trust",
        row3Right: "Build connection",
        row4Left: "Fear-based",
        row4Right: "Emotion-based"
      },
      privacy: {
        kicker: "Privacy first",
        headline: "Your child's data stays in the family.",
        point1: "No ads",
        point2: "No profiling",
        point3: "No selling data",
        point4: "No third-party tracking",
        firewall: "Rulee acts as a privacy firewall between families and AI models.",
        providersCannotSee: "AI providers never see who the child is, family identity, or personal data."
      },
      insights: {
        kicker: "Parent view",
        headline: "What parents actually see",
        card1Label: "Rulee Insight",
        card1Body: "Your child has been exploring space and astronomy lately. This is common curiosity for this age. You could ask them what planet they find most interesting.",
        card2Label: "Rulee Suggestion",
        card2Body: "Tonight could be a good moment for a short walk or drawing together."
      },
      trust: {
        kicker: "Trust and control",
        headline: "Built for families, not data collection",
        point1: "Privacy-first architecture",
        point2: "Parent-controlled accounts",
        point3: "No social media integrations",
        point4: "Easy delete anytime"
      },
      pricing: {
        kicker: "Simple pricing",
        headline: "Pricing for every family",
        freeTitle: "Free",
        freeBody: "Basic AI access.",
        familyTitle: "Family",
        familyBody: "Full insights for parents.",
        premiumTitle: "Premium",
        premiumBody: "Advanced parent guidance and suggestions."
      },
      finalCta: {
        headline: "Raise curious kids without losing connection.",
        button: "Start your family account"
      },
      footer: {
        privacyPolicy: "Privacy Policy",
        safetyInfo: "Safety Information",
        parentFaq: "Parent FAQ",
        contact: "Contact",
        terms: "Terms"
      }
    },
    
    // Subjects
    subjects: {
      math: {
        name: "Mathematics",
        description: "Numbers, calculations, and problem solving"
      },
      science: {
        name: "Science",
        description: "Discover the world around us"
      },
      english: {
        name: "English",
        description: "Reading, writing, and communication"
      },
      history: {
        name: "History",
        description: "Learn about the past"
      },
      geography: {
        name: "Geography",
        description: "Explore the world and places"
      },
      art: {
        name: "Art",
        description: "Creativity and expression"
      },
      music: {
        name: "Music",
        description: "Sounds, rhythm, and melodies"
      },
      pe: {
        name: "Physical Education",
        description: "Sports and staying active"
      }
    }
  },
  
  sr: {
    // Chat component
    welcome: "Dobrodošli u RuleeAI",
    navChat: "Ćaskanje",
    navQuiz: "Kviz",
    navConversations: "Razgovori",
    navSubjects: "Školski Predmeti",
    navParentDashboard: "Roditeljska Kontrolna Tabla",
    menu: "Meni",
    openMenu: "Otvori meni",
    closeMenu: "Zatvori meni",
    logout: "Odjava",
    newChat: "Novi Chat",
    myConversations: "Moji razgovori",
    convoFilterAll: "Svi",
    convoFilterGeneral: "Opšti chat",
    convoFilterSubjectLearning: "Učenje predmeta",
    convoFilterSectionLearning: "Učenje odeljka",
    convoFilterQuiz: "Kvizovi",
    convoFilterTodaySection: "Današnji odseci",
    loadingConversations: "Učitavanje razgovora...",
    noConversationsYet: "Još nema razgovora. Pokreni novi chat da počneš!",
    startNewChat: "Pokreni novi chat",
    untitledConversation: "Razgovor bez naslova",
    messageSingular: "poruka",
    messagePlural: "poruke",
    selectConversationHint: "Izaberi razgovor iz taba Razgovori ili pokreni novi chat!",
    schoolClasses: "Školski Predmeti",
    subjectsDescription: "Izaberite predmet da počnete učenje",
    schoolClassesDescription: "Izaberite predmet da počnete učenje",
    loadingClasses: "Učitavanje predmeta...",
    noClassesAssigned: "Još nema dodeljenih predmeta.",
    askParentAssignClasses: "Zamoli roditelja da ti dodeli predmete.",
    addTodaysSection: "Dodaj Današnji Odsek",
    learnSection: "Uči Odsek",
    selectSection: "Izaberi odsek za učenje",
    learnThis: "Uči ovo",
    back: "Nazad",
    generateQuiz: "Generiši Kviz",
    send: "Pošalji",
    newConversation: "Nova Konverzacija",
    typeMessage: "Ukucajte poruku...",
    loading: "Učitavanje...",
    errorSendingMessage: "Greška pri slanju poruke. Molimo pokušajte ponovo.",
    pleaseFillFields: "Molimo popunite oba polja - temu i odsek.",
    you: "Ti",
    assistant: "Asistent",
    assistantTyping: "Asistent kuca...",
    closeConversation: "Zatvori konverzaciju",
    closeModal: "Zatvori prozor",
    closeAlert: "Zatvori obaveštenje",
    closeReviewModal: "Zatvori pregled kviza",
    copyCode: "Kopiraj kod",
    switchToLightMode: "Prebaci na svetlu temu",
    switchToDarkMode: "Prebaci na tamnu temu",
    loginToRulee: "Prijava na RuleeAI",
    username: "Korisničko ime",
    password: "Lozinka",
    loggingIn: "Prijavljivanje...",
    login: "Prijava",
    backToHome: "Nazad na početnu",
    noticeTitle: "Obaveštenje",
    okLabel: "U redu",
    
    // Subject modal
    selectSubject: "Izaberite predmet",
    topic: "Tema",
    section: "Odsek",
    enterTopic: "Unesite temu koju ste naučili",
    enterSection: "Unesite odsek koji ste pokrili",
    submit: "Pošalji",
    cancel: "Otkaži",
    addTodaysSectionDesc: "Dodaj ono što si danas naučio na času",
    learnSectionDesc: "Kreiraj putanju učenja i uči korak po korak",
    generateQuizDesc: "Vežbaj sa zabavnim kvizom na osnovu tvog napretka",
    addTodaysSectionMessage: "Želim da dodam današnji odsek za",
    helpUnderstandMessage: "Pomoz mi da razumem i zapamtim ono što smo danas naučili.",
    learnAboutMessage: "Želim da učim o",
    createLearningPathMessage: "Kreiraj putanju učenja za mene i pomozi mi da razumem teme korak po korak.",
    noTopicsForQuiz: "Nema tema za ovaj predmet. Molimo dodajte teme koristeći 'Dodaj Današnji Odsek' prvo.",
    quizSourceLabel: "Izvor kviza",
    quizSourceAuto: "Auto (najbolji dostupni)",
    quizSourceWeakTopics: "Samo slabe teme",
    quizSourceKnowledgeTopics: "Teme iz znanja",
    quizSourceTodaySections: "Današnji odseci",
    quizQuestionCountLabel: "Broj pitanja",
    quizGeneratingTitle: "Generišem novi kviz...",
    quizGeneratingDescription: "Sačekaj trenutak dok pripremimo pitanja za tebe.",
    quizSectionsLabel: "Izaberi odseke za kviz",
    selectQuizSectionsValidation: "Izaberi bar jedan odsek.",
    noSectionsFound: "Još nema odeljaka za ovaj predmet. Prvo dodaj današnji odsek, pa onda pokreni učenje ili kviz.",
    quizNoActive: "Nema aktivnog kviza. Generiši ga iz Školskih Predmeta.",
    quizPageTitle: "Kviz",
    quizStartNewTitle: "Pokreni novi kviz",
    quizStartNewAction: "Pokreni novi kviz",
    quizShowSetup: "Prikaži podešavanja kviza",
    quizHideSetup: "Sakrij podešavanja kviza",
    quizShowHistory: "Prikaži prethodne kvizove",
    quizHideHistory: "Sakrij prethodne kvizove",
    quizShowActive: "Prikaži aktivne kvizove",
    quizHideActive: "Sakrij aktivne kvizove",
    quizHistoryTitle: "Završeni kvizovi",
    quizHistoryEmpty: "Još nema završenih kvizova.",
    quizActiveTitle: "Aktivni kvizovi",
    quizActiveEmpty: "Još nema aktivnih kvizova.",
    quizOpenActive: "Otvori kviz",
    quizOpeningActive: "Otvaranje kviza...",
    quizLoadingReview: "Učitavam pregled kviza...",
    quizReviewNext: "Sledeći",
    quizReviewPrevious: "Prethodni",
    quizSubjectLabel: "Predmet",
    backToSubjects: "Nazad na predmete",
    quizQuestions: "pitanja",
    quizResultTitle: "Rezultat kviza",
    quizScoreLabel: "rezultat",
    quizTopicsToPractice: "Teme za vežbu",
    quizStatusCorrect: "Tačno",
    quizStatusIncorrect: "Netačno",
    quizYourAnswerLabel: "Tvoj odgovor",
    quizNoAnswerGiven: "Nije dat odgovor",
    quizCorrectAnswerLabel: "Tačan odgovor",
    quizCorrectAnswerMissing: "Odgovor nije naveden",
    quizTextAnswerPlaceholder: "Upiši odgovor...",
    submitQuiz: "Pošalji kviz",
    cancelQuiz: "Otkaži kviz",
    quizCompleteAllQuestions: "Odgovori na sva pitanja pre slanja.",
    
    // Subjects
    subjects: {
      math: {
        name: "Matematika",
        description: "Brojevi, računanje i rešavanje problema"
      },
      science: {
        name: "Priroda i Društvo",
        description: "Otkrijte svet oko nas"
      },
      english: {
        name: "Engleski Jezik",
        description: "Čitanje, pisanje i komunikacija"
      },
      history: {
        name: "Istorija",
        description: "Učite o prošlosti"
      },
      geography: {
        name: "Geografija",
        description: "Istražite svet i mesta"
      },
      art: {
        name: "Likovno",
        description: "Kreativnost i izražavanje"
      },
      music: {
        name: "Muzičko",
        description: "Zvukovi, ritam i melodije"
      },
      pe: {
        name: "Fizičko Vaspitanje",
        description: "Sport i aktivnost"
      }
    }
  },
  
  es: {
    // Chat component
    welcome: "Bienvenido a RuleeAI",
    schoolClasses: "Clases Escolares",
    schoolClassesDescription: "Elige una materia para comenzar a aprender",
    addTodaysSection: "Agregar Sección de Hoy",
    learnSection: "Aprender Sección",
    generateQuiz: "Generar Quiz",
    send: "Enviar",
    newConversation: "Nueva Conversación",
    typeMessage: "Escribe tu mensaje...",
    loading: "Cargando...",
    errorSendingMessage: "Error al enviar mensaje. Por favor intenta de nuevo.",
    pleaseFillFields: "Por favor completa ambos campos: tema y sección.",
    you: "Tú",
    assistant: "Asistente",
    assistantTyping: "El asistente está escribiendo...",
    
    // Subject modal
    selectSubject: "Selecciona una materia",
    topic: "Tema",
    section: "Sección",
    enterTopic: "Ingresa el tema que aprendiste",
    enterSection: "Ingresa la sección que cubriste",
    submit: "Enviar",
    cancel: "Cancelar",
    
    // Subjects
    subjects: {
      math: {
        name: "Matemáticas",
        description: "Números, cálculos y resolución de problemas"
      },
      science: {
        name: "Ciencias",
        description: "Descubre el mundo que nos rodea"
      },
      english: {
        name: "Inglés",
        description: "Lectura, escritura y comunicación"
      },
      history: {
        name: "Historia",
        description: "Aprende sobre el pasado"
      },
      geography: {
        name: "Geografía",
        description: "Explora el mundo y los lugares"
      },
      art: {
        name: "Arte",
        description: "Creatividad y expresión"
      },
      music: {
        name: "Música",
        description: "Sonidos, ritmo y melodías"
      },
      pe: {
        name: "Educación Física",
        description: "Deportes y mantenerse activo"
      }
    }
  },
  
  fr: {
    // Chat component
    welcome: "Bienvenue dans RuleeAI",
    schoolClasses: "Cours Scolaires",
    schoolClassesDescription: "Choisissez une matière pour commencer à apprendre",
    addTodaysSection: "Ajouter la Section d'Aujourd'hui",
    learnSection: "Apprendre la Section",
    generateQuiz: "Générer un Quiz",
    send: "Envoyer",
    newConversation: "Nouvelle Conversation",
    typeMessage: "Tapez votre message...",
    loading: "Chargement...",
    errorSendingMessage: "Erreur lors de l'envoi du message. Veuillez réessayer.",
    pleaseFillFields: "Veuillez remplir les deux champs: sujet et section.",
    you: "Vous",
    assistant: "Assistant",
    assistantTyping: "L'assistant tape...",
    
    // Subject modal
    selectSubject: "Sélectionnez une matière",
    topic: "Sujet",
    section: "Section",
    enterTopic: "Entrez le sujet que vous avez appris",
    enterSection: "Entrez la section que vous avez couverte",
    submit: "Envoyer",
    cancel: "Annuler",
    addTodaysSectionDesc: "Ajoutez ce que vous avez appris en classe aujourd'hui",
    learnSectionDesc: "Créez un parcours d'apprentissage et étudiez étape par étape",
    generateQuizDesc: "Pratiquez avec un jeu de quiz amusant basé sur vos progrès",
    addTodaysSectionMessage: "Je veux ajouter la section d'aujourd'hui pour",
    helpUnderstandMessage: "Aidez-moi à comprendre et à me souvenir de ce que nous avons appris aujourd'hui.",
    learnAboutMessage: "Je veux apprendre sur",
    createLearningPathMessage: "Créez un parcours d'apprentissage pour moi et aidez-moi à comprendre les sujets étape par étape.",
    
    // Subjects
    subjects: {
      math: {
        name: "Mathématiques",
        description: "Nombres, calculs et résolution de problèmes"
      },
      science: {
        name: "Sciences",
        description: "Découvrez le monde qui nous entoure"
      },
      english: {
        name: "Anglais",
        description: "Lecture, écriture et communication"
      },
      history: {
        name: "Histoire",
        description: "Apprenez sur le passé"
      },
      geography: {
        name: "Géographie",
        description: "Explorez le monde et les lieux"
      },
      art: {
        name: "Art",
        description: "Créativité et expression"
      },
      music: {
        name: "Musique",
        description: "Sons, rythme et mélodies"
      },
      pe: {
        name: "Éducation Physique",
        description: "Sports et rester actif"
      }
    }
  },
  
  de: {
    // Chat component
    welcome: "Willkommen bei RuleeAI",
    schoolClasses: "Schulfächer",
    schoolClassesDescription: "Wähle ein Fach zum Lernen",
    addTodaysSection: "Heutigen Abschnitt Hinzufügen",
    learnSection: "Abschnitt Lernen",
    generateQuiz: "Quiz Generieren",
    send: "Senden",
    newConversation: "Neue Unterhaltung",
    typeMessage: "Nachricht eingeben...",
    loading: "Lädt...",
    errorSendingMessage: "Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.",
    pleaseFillFields: "Bitte füllen Sie beide Felder aus: Thema und Abschnitt.",
    you: "Sie",
    assistant: "Assistent",
    assistantTyping: "Der Assistent tippt...",
    
    // Subject modal
    selectSubject: "Wähle ein Fach",
    topic: "Thema",
    section: "Abschnitt",
    enterTopic: "Geben Sie das gelernte Thema ein",
    enterSection: "Geben Sie den behandelten Abschnitt ein",
    submit: "Senden",
    cancel: "Abbrechen",
    addTodaysSectionDesc: "Fügen Sie hinzu, was Sie heute im Unterricht gelernt haben",
    learnSectionDesc: "Erstellen Sie einen Lernpfad und lernen Sie Schritt für Schritt",
    generateQuizDesc: "Üben Sie mit einem unterhaltsamen Quiz-Spiel basierend auf Ihrem Fortschritt",
    addTodaysSectionMessage: "Ich möchte den heutigen Abschnitt hinzufügen für",
    helpUnderstandMessage: "Helfen Sie mir zu verstehen und mich an das zu erinnern, was wir heute gelernt haben.",
    learnAboutMessage: "Ich möchte lernen über",
    createLearningPathMessage: "Erstellen Sie einen Lernpfad für mich und helfen Sie mir, die Themen Schritt für Schritt zu verstehen.",
    
    // Subjects
    subjects: {
      math: {
        name: "Mathematik",
        description: "Zahlen, Berechnungen und Problemlösung"
      },
      science: {
        name: "Naturwissenschaften",
        description: "Entdecke die Welt um uns herum"
      },
      english: {
        name: "Englisch",
        description: "Lesen, Schreiben und Kommunikation"
      },
      history: {
        name: "Geschichte",
        description: "Lerne über die Vergangenheit"
      },
      geography: {
        name: "Geographie",
        description: "Erkunde die Welt und Orte"
      },
      art: {
        name: "Kunst",
        description: "Kreativität und Ausdruck"
      },
      music: {
        name: "Musik",
        description: "Klänge, Rhythmus und Melodien"
      },
      pe: {
        name: "Sport",
        description: "Sport und aktiv bleiben"
      }
    }
  },
  
  it: {
    // Chat component
    welcome: "Benvenuto in RuleeAI",
    schoolClasses: "Materie Scolastiche",
    schoolClassesDescription: "Scegli una materia per iniziare ad imparare",
    addTodaysSection: "Aggiungi Sezione di Oggi",
    learnSection: "Impara Sezione",
    generateQuiz: "Genera Quiz",
    send: "Invia",
    newConversation: "Nuova Conversazione",
    typeMessage: "Scrivi il tuo messaggio...",
    loading: "Caricamento...",
    errorSendingMessage: "Errore nell'invio del messaggio. Riprova.",
    pleaseFillFields: "Compila entrambi i campi: argomento e sezione.",
    you: "Tu",
    assistant: "Assistente",
    assistantTyping: "L'assistente sta scrivendo...",
    
    // Subject modal
    selectSubject: "Seleziona una materia",
    topic: "Argomento",
    section: "Sezione",
    enterTopic: "Inserisci l'argomento che hai imparato",
    enterSection: "Inserisci la sezione che hai coperto",
    submit: "Invia",
    cancel: "Annulla",
    addTodaysSectionDesc: "Aggiungi ciò che hai imparato in classe oggi",
    learnSectionDesc: "Crea un percorso di apprendimento e studia passo dopo passo",
    generateQuizDesc: "Pratica con un gioco quiz divertente basato sui tuoi progressi",
    addTodaysSectionMessage: "Voglio aggiungere la sezione di oggi per",
    helpUnderstandMessage: "Aiutami a capire e ricordare ciò che abbiamo imparato oggi.",
    learnAboutMessage: "Voglio imparare su",
    createLearningPathMessage: "Crea un percorso di apprendimento per me e aiutami a capire gli argomenti passo dopo passo.",
    
    // Subjects
    subjects: {
      math: {
        name: "Matematica",
        description: "Numeri, calcoli e risoluzione di problemi"
      },
      science: {
        name: "Scienze",
        description: "Scopri il mondo intorno a noi"
      },
      english: {
        name: "Inglese",
        description: "Lettura, scrittura e comunicazione"
      },
      history: {
        name: "Storia",
        description: "Impara sul passato"
      },
      geography: {
        name: "Geografia",
        description: "Esplora il mondo e i luoghi"
      },
      art: {
        name: "Arte",
        description: "Creatività ed espressione"
      },
      music: {
        name: "Musica",
        description: "Suoni, ritmo e melodie"
      },
      pe: {
        name: "Educazione Fisica",
        description: "Sport e rimanere attivi"
      }
    }
  },
  
  pt: {
    // Chat component
    welcome: "Bem-vindo ao RuleeAI",
    schoolClasses: "Matérias Escolares",
    schoolClassesDescription: "Escolha uma matéria para começar a aprender",
    addTodaysSection: "Adicionar Seção de Hoje",
    learnSection: "Aprender Seção",
    generateQuiz: "Gerar Quiz",
    send: "Enviar",
    newConversation: "Nova Conversa",
    typeMessage: "Digite sua mensagem...",
    loading: "Carregando...",
    errorSendingMessage: "Erro ao enviar mensagem. Por favor, tente novamente.",
    pleaseFillFields: "Por favor, preencha ambos os campos: tópico e seção.",
    
    // Subject modal
    selectSubject: "Selecione uma matéria",
    topic: "Tópico",
    section: "Seção",
    enterTopic: "Digite o tópico que você aprendeu",
    enterSection: "Digite a seção que você cobriu",
    submit: "Enviar",
    cancel: "Cancelar",
    
    // Subjects
    subjects: {
      math: {
        name: "Matemática",
        description: "Números, cálculos e resolução de problemas"
      },
      science: {
        name: "Ciências",
        description: "Descubra o mundo ao nosso redor"
      },
      english: {
        name: "Inglês",
        description: "Leitura, escrita e comunicação"
      },
      history: {
        name: "História",
        description: "Aprenda sobre o passado"
      },
      geography: {
        name: "Geografia",
        description: "Explore o mundo e lugares"
      },
      art: {
        name: "Arte",
        description: "Criatividade e expressão"
      },
      music: {
        name: "Música",
        description: "Sons, ritmo e melodias"
      },
      pe: {
        name: "Educação Física",
        description: "Esportes e manter-se ativo"
      }
    }
  },
  
  ru: {
    // Chat component
    welcome: "Добро пожаловать в RuleeAI",
    schoolClasses: "Школьные Предметы",
    schoolClassesDescription: "Выберите предмет для начала обучения",
    addTodaysSection: "Добавить Сегодняшний Раздел",
    learnSection: "Изучить Раздел",
    generateQuiz: "Создать Викторину",
    send: "Отправить",
    newConversation: "Новый Разговор",
    typeMessage: "Введите ваше сообщение...",
    loading: "Загрузка...",
    errorSendingMessage: "Ошибка при отправке сообщения. Пожалуйста, попробуйте снова.",
    pleaseFillFields: "Пожалуйста, заполните оба поля: тема и раздел.",
    
    // Subject modal
    selectSubject: "Выберите предмет",
    topic: "Тема",
    section: "Раздел",
    enterTopic: "Введите тему, которую вы изучили",
    enterSection: "Введите раздел, который вы прошли",
    submit: "Отправить",
    cancel: "Отмена",
    
    // Subjects
    subjects: {
      math: {
        name: "Математика",
        description: "Числа, вычисления и решение задач"
      },
      science: {
        name: "Наука",
        description: "Откройте мир вокруг нас"
      },
      english: {
        name: "Английский",
        description: "Чтение, письмо и общение"
      },
      history: {
        name: "История",
        description: "Узнайте о прошлом"
      },
      geography: {
        name: "География",
        description: "Исследуйте мир и места"
      },
      art: {
        name: "Искусство",
        description: "Творчество и самовыражение"
      },
      music: {
        name: "Музыка",
        description: "Звуки, ритм и мелодии"
      },
      pe: {
        name: "Физкультура",
        description: "Спорт и активность"
      }
    }
  },
  
  zh: {
    // Chat component
    welcome: "欢迎使用 RuleeAI",
    schoolClasses: "学校课程",
    schoolClassesDescription: "选择一门课程开始学习",
    addTodaysSection: "添加今日章节",
    learnSection: "学习章节",
    generateQuiz: "生成测验",
    send: "发送",
    newConversation: "新对话",
    typeMessage: "输入您的消息...",
    loading: "加载中...",
    errorSendingMessage: "发送消息时出错。请重试。",
    pleaseFillFields: "请填写两个字段：主题和章节。",
    
    // Subject modal
    selectSubject: "选择一门课程",
    topic: "主题",
    section: "章节",
    enterTopic: "输入您学习的主题",
    enterSection: "输入您涵盖的章节",
    submit: "提交",
    cancel: "取消",
    
    // Subjects
    subjects: {
      math: {
        name: "数学",
        description: "数字、计算和解决问题"
      },
      science: {
        name: "科学",
        description: "发现我们周围的世界"
      },
      english: {
        name: "英语",
        description: "阅读、写作和交流"
      },
      history: {
        name: "历史",
        description: "了解过去"
      },
      geography: {
        name: "地理",
        description: "探索世界和地点"
      },
      art: {
        name: "艺术",
        description: "创造力和表达"
      },
      music: {
        name: "音乐",
        description: "声音、节奏和旋律"
      },
      pe: {
        name: "体育",
        description: "运动和保持活跃"
      }
    }
  },
  
  ja: {
    // Chat component
    welcome: "RuleeAIへようこそ",
    schoolClasses: "学校の科目",
    schoolClassesDescription: "学習を始める科目を選択してください",
    addTodaysSection: "今日のセクションを追加",
    learnSection: "セクションを学習",
    generateQuiz: "クイズを生成",
    send: "送信",
    newConversation: "新しい会話",
    typeMessage: "メッセージを入力...",
    loading: "読み込み中...",
    errorSendingMessage: "メッセージの送信に失敗しました。もう一度お試しください。",
    pleaseFillFields: "トピックとセクションの両方のフィールドを入力してください。",
    
    // Subject modal
    selectSubject: "科目を選択",
    topic: "トピック",
    section: "セクション",
    enterTopic: "学習したトピックを入力",
    enterSection: "学習したセクションを入力",
    submit: "送信",
    cancel: "キャンセル",
    
    // Subjects
    subjects: {
      math: {
        name: "数学",
        description: "数字、計算、問題解決"
      },
      science: {
        name: "理科",
        description: "私たちの周りの世界を発見"
      },
      english: {
        name: "英語",
        description: "読む、書く、コミュニケーション"
      },
      history: {
        name: "歴史",
        description: "過去について学ぶ"
      },
      geography: {
        name: "地理",
        description: "世界と場所を探索"
      },
      art: {
        name: "美術",
        description: "創造性と表現"
      },
      music: {
        name: "音楽",
        description: "音、リズム、メロディー"
      },
      pe: {
        name: "体育",
        description: "スポーツとアクティブに過ごす"
      }
    }
  },
  
  ko: {
    // Chat component
    welcome: "RuleeAI에 오신 것을 환영합니다",
    schoolClasses: "학교 과목",
    schoolClassesDescription: "학습을 시작할 과목을 선택하세요",
    addTodaysSection: "오늘의 섹션 추가",
    learnSection: "섹션 학습",
    generateQuiz: "퀴즈 생성",
    send: "보내기",
    newConversation: "새 대화",
    typeMessage: "메시지를 입력하세요...",
    loading: "로딩 중...",
    errorSendingMessage: "메시지 전송 중 오류가 발생했습니다. 다시 시도해주세요.",
    pleaseFillFields: "주제와 섹션 필드를 모두 입력해주세요.",
    
    // Subject modal
    selectSubject: "과목 선택",
    topic: "주제",
    section: "섹션",
    enterTopic: "학습한 주제를 입력하세요",
    enterSection: "학습한 섹션을 입력하세요",
    submit: "제출",
    cancel: "취소",
    
    // Subjects
    subjects: {
      math: {
        name: "수학",
        description: "숫자, 계산, 문제 해결"
      },
      science: {
        name: "과학",
        description: "우리 주변의 세계를 발견하세요"
      },
      english: {
        name: "영어",
        description: "읽기, 쓰기, 의사소통"
      },
      history: {
        name: "역사",
        description: "과거에 대해 배우기"
      },
      geography: {
        name: "지리",
        description: "세계와 장소 탐험"
      },
      art: {
        name: "미술",
        description: "창의성과 표현"
      },
      music: {
        name: "음악",
        description: "소리, 리듬, 멜로디"
      },
      pe: {
        name: "체육",
        description: "스포츠와 활동 유지"
      }
    }
  },
  
  ar: {
    // Chat component
    welcome: "مرحباً بك في RuleeAI",
    schoolClasses: "المواد الدراسية",
    schoolClassesDescription: "اختر مادة لبدء التعلم",
    addTodaysSection: "إضافة قسم اليوم",
    learnSection: "تعلم القسم",
    generateQuiz: "إنشاء اختبار",
    send: "إرسال",
    newConversation: "محادثة جديدة",
    typeMessage: "اكتب رسالتك...",
    loading: "جاري التحميل...",
    errorSendingMessage: "خطأ في إرسال الرسالة. يرجى المحاولة مرة أخرى.",
    pleaseFillFields: "يرجى ملء كلا الحقلين: الموضوع والقسم.",
    
    // Subject modal
    selectSubject: "اختر مادة",
    topic: "الموضوع",
    section: "القسم",
    enterTopic: "أدخل الموضوع الذي تعلمته",
    enterSection: "أدخل القسم الذي غطيته",
    submit: "إرسال",
    cancel: "إلغاء",
    
    // Subjects
    subjects: {
      math: {
        name: "الرياضيات",
        description: "الأرقام والحسابات وحل المشكلات"
      },
      science: {
        name: "العلوم",
        description: "اكتشف العالم من حولنا"
      },
      english: {
        name: "اللغة الإنجليزية",
        description: "القراءة والكتابة والتواصل"
      },
      history: {
        name: "التاريخ",
        description: "تعلم عن الماضي"
      },
      geography: {
        name: "الجغرافيا",
        description: "استكشف العالم والأماكن"
      },
      art: {
        name: "الفن",
        description: "الإبداع والتعبير"
      },
      music: {
        name: "الموسيقى",
        description: "الأصوات والإيقاع واللحن"
      },
      pe: {
        name: "التربية البدنية",
        description: "الرياضة والبقاء نشطاً"
      }
    }
  },
  
  hi: {
    // Chat component
    welcome: "RuleeAI में आपका स्वागत है",
    schoolClasses: "स्कूल विषय",
    schoolClassesDescription: "सीखना शुरू करने के लिए एक विषय चुनें",
    addTodaysSection: "आज का अनुभाग जोड़ें",
    learnSection: "अनुभाग सीखें",
    generateQuiz: "क्विज़ बनाएं",
    send: "भेजें",
    newConversation: "नई बातचीत",
    typeMessage: "अपना संदेश टाइप करें...",
    loading: "लोड हो रहा है...",
    errorSendingMessage: "संदेश भेजने में त्रुटि। कृपया पुनः प्रयास करें।",
    pleaseFillFields: "कृपया दोनों फ़ील्ड भरें: विषय और अनुभाग।",
    
    // Subject modal
    selectSubject: "एक विषय चुनें",
    topic: "विषय",
    section: "अनुभाग",
    enterTopic: "आपने जो विषय सीखा है उसे दर्ज करें",
    enterSection: "आपने जो अनुभाग कवर किया है उसे दर्ज करें",
    submit: "जमा करें",
    cancel: "रद्द करें",
    
    // Subjects
    subjects: {
      math: {
        name: "गणित",
        description: "संख्याएँ, गणना और समस्या समाधान"
      },
      science: {
        name: "विज्ञान",
        description: "हमारे आसपास की दुनिया की खोज करें"
      },
      english: {
        name: "अंग्रेजी",
        description: "पढ़ना, लिखना और संचार"
      },
      history: {
        name: "इतिहास",
        description: "अतीत के बारे में जानें"
      },
      geography: {
        name: "भूगोल",
        description: "दुनिया और स्थानों का अन्वेषण करें"
      },
      art: {
        name: "कला",
        description: "रचनात्मकता और अभिव्यक्ति"
      },
      music: {
        name: "संगीत",
        description: "ध्वनियाँ, लय और मधुर स्वर"
      },
      pe: {
        name: "शारीरिक शिक्षा",
        description: "खेल और सक्रिय रहना"
      }
    }
  }
};

/**
 * Get translation for a given key and language
 * @param {string} key - Translation key (supports nested keys with dot notation, e.g., 'subjects.math.name')
 * @param {string} language - Language code (default: 'en')
 * @returns {string} Translated text or the key if translation not found
 */
export const t = (key, language = 'en') => {
  const lang = translations[language] || translations['en'];
  const keys = key.split('.');
  let value = lang;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to English if translation not found
      const enValue = translations['en'];
      let enResult = enValue;
      for (const enK of keys) {
        if (enResult && typeof enResult === 'object' && enK in enResult) {
          enResult = enResult[enK];
        } else {
          return key; // Return key if even English translation not found
        }
      }
      return enResult;
    }
  }
  
  return typeof value === 'string' ? value : key;
};

/**
 * Get all translations for a specific language
 * @param {string} language - Language code (default: 'en')
 * @returns {object} All translations for the language
 */
export const getTranslations = (language = 'en') => {
  return translations[language] || translations['en'];
};

export default translations;

